import { Plugin, Widget } from 'ckeditor5'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { scormGetEntryPointUrlService, scormFilterService } from '../../../../../api/scorm'
import { track } from '@amplitude/analytics-browser'

const DEFAULT_SCORM_CONFIG = {
    blockId: null,
    packageId: null,
    title: '',
    entryPointUrl: '',
    width: '100%',
    height: '500px',
    version: '',
    fileName: '',
}

const R2_BASE_URL =
    process.env.REACT_APP_R2_BASE_URL ||
    process.env.REACT_APP_CDN ||
    process.env.REACT_APP_STORAGE ||
    ''

function generateBlockId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    // RFC4122-ish fallback when crypto.randomUUID is unavailable
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

function getFullEntryPointUrl(entryPointUrl) {
    if (!entryPointUrl) return ''
    if (entryPointUrl.startsWith('http')) return entryPointUrl
    // Relative key — prepend R2 public base URL
    return R2_BASE_URL.replace(/\/$/, '') + '/' + entryPointUrl.replace(/^\//, '')
}

const SCORM_STATUS_LABEL = {
    0: 'Pending',
    1: 'Uploaded',
    2: 'Processing',
    3: 'Ready',
    4: 'Failed',
}

// ── Recent SCORMs picker — paginated list with search ──
const RECENT_SCORMS_PAGE_SIZE = 20

function RecentScormsPanel({ onSelect, onClose }) {
    const [allItems, setAllItems] = useState([])
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Fetch every package once; search + pagination are done client-side so the
    // search matches both title and fileName regardless of backend filtering.
    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError(null)

        ;(async () => {
            try {
                const collected = []
                let currentPage = 0
                // Hard cap to avoid a runaway loop on very large accounts.
                for (let i = 0; i < 50; i++) {
                    const res = await scormFilterService({ currentPage, pageSize: 100 })
                    const data = res && res.data !== undefined ? res.data : res
                    const results = (data && data.results) || []
                    collected.push(...results)
                    if (!(data && data.hasNextPage)) break
                    currentPage += 1
                }
                if (!cancelled) setAllItems(collected)
            } catch (e) {
                if (!cancelled) setError('Failed to load recent SCORMs')
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [])

    // Client-side filter over title + fileName (case-insensitive).
    const q = search.trim().toLowerCase()
    const filtered = q
        ? allItems.filter(pkg => {
            const title = (pkg.title || '').toLowerCase()
            const fileName = (pkg.fileName || '').toLowerCase()
            return title.includes(q) || fileName.includes(q)
        })
        : allItems

    const pageCount = Math.max(1, Math.ceil(filtered.length / RECENT_SCORMS_PAGE_SIZE))
    const safePage = Math.min(page, pageCount - 1)
    const items = filtered.slice(
        safePage * RECENT_SCORMS_PAGE_SIZE,
        safePage * RECENT_SCORMS_PAGE_SIZE + RECENT_SCORMS_PAGE_SIZE,
    )
    const hasNext = safePage < pageCount - 1

    return React.createElement(
        'div',
        {
            style: {
                padding: '20px',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            },
        },
        // Header
        React.createElement(
            'div',
            { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            React.createElement('h4', { style: { margin: 0, fontSize: '15px', color: '#0f172a' } }, 'Recent SCORM packages'),
            React.createElement(
                'button',
                {
                    type: 'button',
                    onClick: onClose,
                    style: {
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#64748b',
                        fontSize: '16px',
                        padding: '4px 8px',
                    },
                    'aria-label': 'Close',
                },
                '✕'
            )
        ),
        // Search input
        React.createElement('input', {
            type: 'text',
            value: search,
            onChange: e => { setSearch(e.target.value); setPage(0) },
            onMouseDown: e => e.stopPropagation(),
            onPointerDown: e => e.stopPropagation(),
            onClick: e => e.stopPropagation(),
            onKeyDown: e => e.stopPropagation(),
            onKeyUp: e => e.stopPropagation(),
            onInput: e => e.stopPropagation(),
            onFocus: e => e.stopPropagation(),
            onBlur: e => e.stopPropagation(),
            'data-cke-ignore-events': 'true',
            placeholder: 'Search by title or filename…',
            style: {
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
            },
        }),
        // List body
        React.createElement(
            'div',
            { style: { flex: 1, minHeight: '180px', maxHeight: '320px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '8px' } },
            loading && items.length === 0
                ? React.createElement('p', { style: { padding: '20px', color: '#64748b', textAlign: 'center', margin: 0 } }, 'Loading…')
                : error
                    ? React.createElement('p', { style: { padding: '20px', color: '#dc2626', textAlign: 'center', margin: 0 } }, error)
                    : items.length === 0
                        ? React.createElement('p', { style: { padding: '20px', color: '#64748b', textAlign: 'center', margin: 0 } }, 'No SCORM packages found')
                        : React.createElement(
                            'ul',
                            { style: { listStyle: 'none', padding: 0, margin: 0 } },
                            items.map(pkg => {
                                const isReady = pkg.status === 3
                                return React.createElement(
                                    'li',
                                    {
                                        key: pkg.id,
                                        onClick: () => { if (isReady) onSelect(pkg) },
                                        style: {
                                            padding: '10px 12px',
                                            borderBottom: '1px solid #f1f5f9',
                                            cursor: isReady ? 'pointer' : 'not-allowed',
                                            opacity: isReady ? 1 : 0.55,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '8px',
                                        },
                                        onMouseEnter: e => { if (isReady) e.currentTarget.style.background = '#f8fafc' },
                                        onMouseLeave: e => { e.currentTarget.style.background = 'transparent' },
                                    },
                                    React.createElement(
                                        'div',
                                        { style: { minWidth: 0, flex: 1 } },
                                        React.createElement(
                                            'div',
                                            { style: { fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                                            pkg.title || pkg.fileName || pkg.id
                                        ),
                                        React.createElement(
                                            'div',
                                            { style: { fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                                            pkg.fileName || ''
                                        )
                                    ),
                                    React.createElement(
                                        'span',
                                        {
                                            style: {
                                                fontSize: '11px',
                                                padding: '2px 8px',
                                                borderRadius: '999px',
                                                background: isReady ? '#dcfce7' : pkg.status === 4 ? '#fee2e2' : '#fef9c3',
                                                color: isReady ? '#166534' : pkg.status === 4 ? '#991b1b' : '#854d0e',
                                                fontWeight: 600,
                                                flexShrink: 0,
                                            },
                                        },
                                        SCORM_STATUS_LABEL[pkg.status] || 'Unknown'
                                    )
                                )
                            })
                        )
        ),
        // Pagination
        React.createElement(
            'div',
            { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' } },
            React.createElement(
                'button',
                {
                    type: 'button',
                    disabled: safePage === 0 || loading,
                    onClick: () => setPage(p => Math.max(0, p - 1)),
                    style: {
                        padding: '6px 12px',
                        background: '#f1f5f9',
                        color: safePage === 0 ? '#cbd5e1' : '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                    },
                },
                '← Previous'
            ),
            React.createElement('span', { style: { fontSize: '12px', color: '#64748b' } }, `Page ${safePage + 1} / ${pageCount}`),
            React.createElement(
                'button',
                {
                    type: 'button',
                    disabled: !hasNext || loading,
                    onClick: () => setPage(p => p + 1),
                    style: {
                        padding: '6px 12px',
                        background: '#f1f5f9',
                        color: !hasNext ? '#cbd5e1' : '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        cursor: !hasNext ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                    },
                },
                'Next →'
            )
        )
    )
}

// ── Inline React component for the editor widget ──
function ScormEditorWidget({ config, editor }) {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isRecentOpen, setIsRecentOpen] = useState(false)
    const [editing, setEditing] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadPercent, setUploadPercent] = useState(0)
    const [scormSrc, setScormSrc] = useState(null)
    const [loadingContent, setLoadingContent] = useState(false)
    const [reportedHeight, setReportedHeight] = useState(0)
    const fileInputRef = useRef(null)
    const iframeRef = useRef(null)
    const sourceHeightsRef = useRef(new Map())

    const hasPackage = config && config.packageId

    // Cross-origin iframe (R2 public domain) — we cannot read contentDocument.
    // Backend injects a height-reporter into every SCORM HTML that posts
    // { type: 'scorm-height', height } to window.top. Multiple sources may report
    // (Rustici outer launcher + inner content); take max, reject tiny (~150px) values.
    useEffect(() => {
        function extractHeight(data) {
            if (data == null) return null
            if (typeof data === 'string') {
                if (data.indexOf('[iFrameSizer]') === 0) {
                    const parts = data.split(':')
                    const last = parseFloat(parts[parts.length - 1])
                    return Number.isFinite(last) ? last : null
                }
                return null
            }
            if (typeof data !== 'object') return null
            if (data.type === 'scorm-height' && typeof data.height === 'number') return data.height
            if (typeof data.height === 'number' && data.height > 0) return data.height
            if (data.iframeHeight && Number.isFinite(Number(data.iframeHeight))) return Number(data.iframeHeight)
            return null
        }

        function onMsg(e) {
            const h = extractHeight(e.data)
            if (h == null || !Number.isFinite(h) || h < 300) return
            if (e.source) {
                sourceHeightsRef.current.set(e.source, h)
                const values = Array.from(sourceHeightsRef.current.values())
                const max = values.length > 0 ? Math.max.apply(null, values) : 0
                setReportedHeight(prev => (Math.abs(prev - max) > 1 ? max : prev))
            } else {
                setReportedHeight(prev => (Math.abs(prev - h) > 1 ? h : prev))
            }
        }
        window.addEventListener('message', onMsg)
        return () => window.removeEventListener('message', onMsg)
    }, [])

    // Reset reported heights when the package changes (replace / new SCORM)
    useEffect(() => {
        sourceHeightsRef.current = new Map()
        setReportedHeight(0)
    }, [config && config.packageId, config && config.updatedAt])

    const iframeHeight = reportedHeight >= 300
        ? reportedHeight + 'px'
        : (config && config.height && config.height !== 'auto'
            ? config.height
            : '80vh')

    const handleUpload = useCallback((file) => {
        if (!file || !file.name.toLowerCase().endsWith('.zip')) {
            return
        }

        track('scorm_upload_started', {
            file_name: file.name,
            file_size: file.size,
        })

        setUploading(true)
        setUploadPercent(0)

        const dispatch = editor.config.get('scormDispatch')
        if (dispatch) {
            const blockId = (config && config.blockId) || generateBlockId()
            const isReplace = editing && config && !!config.packageId
            const scormId = isReplace ? config.packageId : null

            const applyResultToModel = (response) => {
                const newConfig = isReplace
                    ? {
                          ...config,
                          blockId: response.blockId || blockId,
                          packageId: response.id || scormId,
                          title: response.title || config.title || file.name.replace(/\.zip$/i, ''),
                          entryPointUrl: response.entryPointUrl || '',
                          version: response.version || config.version || '',
                          fileName: response.fileName || file.name,
                          updatedAt: Date.now(),
                      }
                    : {
                          blockId: response.blockId || blockId,
                          packageId: response.id,
                          title: response.title || file.name.replace(/\.zip$/i, ''),
                          entryPointUrl: response.entryPointUrl || '',
                          width: '100%',
                          height: 'auto',
                          version: response.version || '',
                          fileName: response.fileName || file.name,
                      }

                const selection = editor.model.document.selection
                let scormElement = selection.getSelectedElement()
                if (!scormElement || scormElement.name !== 'scormPackage') {
                    const root = editor.model.document.getRoot()
                    const range = editor.model.createRangeIn(root)
                    for (const value of range.getWalker()) {
                        if (value.item.is && value.item.is('element', 'scormPackage')) {
                            const c = value.item.getAttribute('scormConfig')
                            try {
                                const parsed = c ? JSON.parse(c) : null
                                if (parsed && parsed.blockId === blockId) {
                                    scormElement = value.item
                                    break
                                }
                            } catch (e) { /* ignore */ }
                        }
                    }
                }
                if (scormElement) {
                    editor.model.change(writer => {
                        writer.setAttribute('scormConfig', JSON.stringify(newConfig), scormElement)
                    })
                }
            }

            const callback = (response, error) => {
                setUploading(false)
                setUploadPercent(0)
                if (error || !response) {
                    track('scorm_upload_failed', {
                        file_name: file.name,
                        file_size: file.size,
                        is_replace: isReplace,
                    })
                    return
                }
                track('scorm_file_uploaded', {
                    file_name: file.name,
                    file_size: file.size,
                    version: response.version || '',
                    is_replace: isReplace,
                    package_id: response.id,
                })
                applyResultToModel(response)
                setEditing(false)
            }

            const actions = require('../../../../../reducers/scorm/action')
            if (isReplace) {
                dispatch(actions.scormReplaceAction(file, scormId, blockId, callback, setUploadPercent))
            } else {
                dispatch(actions.scormUploadAction(file, blockId, callback, setUploadPercent))
            }
        } else {
            // Fallback when no dispatch
            setTimeout(() => {
                setUploading(false)
                setUploadPercent(0)
            }, 1500)
        }
    }, [editor, config, editing])

    const handleSelectRecent = useCallback((pkg) => {
        if (!pkg || !pkg.id) return
        const blockId = (config && config.blockId) || generateBlockId()
        const newConfig = {
            ...(config || DEFAULT_SCORM_CONFIG),
            blockId,
            packageId: pkg.id,
            title: pkg.title || pkg.fileName || '',
            entryPointUrl: pkg.entryPointUrl || '',
            version: pkg.version || '',
            fileName: pkg.fileName || '',
            // Cache-bust: when selecting a recent SCORM, force the entry resolver to refetch
            updatedAt: Date.now(),
        }

        const selection = editor.model.document.selection
        let scormElement = selection.getSelectedElement()
        if (!scormElement || scormElement.name !== 'scormPackage') {
            const root = editor.model.document.getRoot()
            const range = editor.model.createRangeIn(root)
            for (const value of range.getWalker()) {
                if (value.item.is && value.item.is('element', 'scormPackage')) {
                    const c = value.item.getAttribute('scormConfig')
                    try {
                        const parsed = c ? JSON.parse(c) : null
                        if (parsed && parsed.blockId === blockId) {
                            scormElement = value.item
                            break
                        }
                    } catch (e) { /* ignore */ }
                }
            }
        }
        if (scormElement) {
            editor.model.change(writer => {
                writer.setAttribute('scormConfig', JSON.stringify(newConfig), scormElement)
            })
        }
        setIsRecentOpen(false)
        setEditing(false)
    }, [editor, config])

    // ✕ click — enter edit mode WITHOUT deleting. The package only gets deleted
    // when the user actually uploads a new file (Replace flow), or when they
    // pick a different one from Recent (snapshot listener fires DELETE for the
    // orphaned packageId). Cancel returns to iframe view without touching backend.
    const enterEditMode = useCallback(() => {
        if (!config || !config.packageId) return
        setEditing(true)
    }, [config])

    const cancelEditMode = useCallback(() => {
        setEditing(false)
    }, [])

    // Resolve entryPointUrl dynamically via the public EntryPointUrl endpoint,
    // then fetch the SCORM HTML and inject API stubs + CSS overrides.
    useEffect(() => {
        if (!hasPackage || !config.packageId) return

        let cancelled = false
        let pollTimer = null

        const STATUS_READY = 3
        const POLL_INTERVAL_MS = 2000
        const POLL_TIMEOUT_MS = 120_000

        const resolve = (startedAt) => {
            scormGetEntryPointUrlService(config.packageId)
                .then(res => {
                    const data = res && res.data !== undefined ? res.data : res
                    const ready =
                        data &&
                        data.entryPointUrl &&
                        (data.status === undefined || data.status === STATUS_READY)
                    if (cancelled) return
                    if (ready) {
                        const fullUrl = data.entryPointUrl.startsWith('http')
                            ? data.entryPointUrl
                            : getFullEntryPointUrl(data.entryPointUrl)
                        // Cache-bust on replace so the iframe reloads when content changes
                        const versioned = config.updatedAt
                            ? fullUrl + (fullUrl.includes('?') ? '&' : '?') + 'v=' + config.updatedAt
                            : fullUrl
                        setScormSrc(versioned)
                        setLoadingContent(false)
                        return
                    }
                    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                        setLoadingContent(false)
                        return
                    }
                    pollTimer = setTimeout(() => resolve(startedAt), POLL_INTERVAL_MS)
                })
                .catch(() => {
                    if (cancelled) return
                    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                        setLoadingContent(false)
                        return
                    }
                    pollTimer = setTimeout(() => resolve(startedAt), POLL_INTERVAL_MS)
                })
        }

        // Only show full loading state on first fetch — on replace, keep old iframe visible
        if (!scormSrc) setLoadingContent(true)
        resolve(Date.now())

        return () => {
            cancelled = true
            if (pollTimer) clearTimeout(pollTimer)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPackage, config.packageId, config.updatedAt])

    if (isModalOpen) {
        return React.createElement(ScormConfigModal, {
            config,
            editor,
            onClose: () => setIsModalOpen(false),
        })
    }

    if (isRecentOpen) {
        return React.createElement(RecentScormsPanel, {
            onSelect: handleSelectRecent,
            onClose: () => setIsRecentOpen(false),
        })
    }

    // No package OR editing existing — show upload placeholder.
    // In edit mode, uploading a new file triggers the Replace flow (old backend
    // package is cleaned up automatically). The placeholder also offers Cancel
    // and Recent picker.
    if (!hasPackage || editing) {
        return React.createElement(
            'div',
            {
                style: {
                    padding: '40px 20px',
                    background: uploading
                        ? '#f8fafc'
                        : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    border: '2px dashed #0ea5e9',
                    borderRadius: '12px',
                    textAlign: 'center',
                    cursor: uploading ? 'default' : 'pointer',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                },
                onClick: () => {
                    if (!uploading && fileInputRef.current) {
                        fileInputRef.current.click()
                    }
                },
                onDragOver: e => {
                    // Required to make this div a valid drop target — otherwise
                    // the browser falls back to opening the file in a new tab
                    // and CKEditor's default upload handler shows an error.
                    e.preventDefault()
                    e.stopPropagation()
                },
                onDragEnter: e => {
                    e.preventDefault()
                    e.stopPropagation()
                },
                onDrop: e => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (uploading) return
                    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
                    if (file) handleUpload(file)
                },
            },
            uploading
                ? React.createElement(
                      'div',
                      { style: { width: '60%', maxWidth: '320px' } },
                      React.createElement('div', {
                          style: {
                              width: '40px',
                              height: '40px',
                              border: '3px solid #e2e8f0',
                              borderTopColor: '#0ea5e9',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              margin: '0 auto 12px',
                          },
                      }),
                      React.createElement(
                          'p',
                          { style: { margin: '0 0 8px', color: '#64748b', fontWeight: 600 } },
                          uploadPercent < 100
                              ? `Uploading… ${uploadPercent}%`
                              : 'Processing…'
                      ),
                      React.createElement(
                          'div',
                          {
                              style: {
                                  width: '100%',
                                  height: '6px',
                                  background: '#e2e8f0',
                                  borderRadius: '3px',
                                  overflow: 'hidden',
                              },
                          },
                          React.createElement('div', {
                              style: {
                                  width: `${uploadPercent}%`,
                                  height: '100%',
                                  background: '#0ea5e9',
                                  transition: 'width 200ms ease',
                              },
                          })
                      )
                  )
                : React.createElement(
                      'div',
                      {},
                      React.createElement(
                          'div',
                          { style: { fontSize: '48px' } },
                          '\uD83D\uDCE6'
                      ),
                      React.createElement(
                          'p',
                          {
                              style: {
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  color: '#0369a1',
                                  margin: 0,
                              },
                          },
                          editing ? 'Replace SCORM Package' : 'SCORM Package'
                      ),
                      React.createElement(
                          'p',
                          {
                              style: {
                                  fontSize: '13px',
                                  color: '#64748b',
                                  margin: 0,
                              },
                          },
                          editing
                              ? 'Click to select a new .zip \u2014 the old one will be replaced'
                              : 'Click to select a SCORM .zip package'
                      ),
                      // Action row: Recent + (editing) Cancel
                      !uploading && React.createElement(
                          'div',
                          {
                              style: {
                                  marginTop: '14px',
                                  display: 'flex',
                                  gap: '12px',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  color: '#0369a1',
                              },
                              onClick: e => e.stopPropagation(),
                          },
                          React.createElement(
                              'button',
                              {
                                  type: 'button',
                                  style: {
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#0369a1',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      fontSize: '12px',
                                      padding: '2px 6px',
                                  },
                                  onClick: e => {
                                      e.stopPropagation()
                                      setIsRecentOpen(true)
                                  },
                              },
                              'Choose from recent'
                          ),
                          editing && React.createElement(
                              'button',
                              {
                                  type: 'button',
                                  style: {
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#64748b',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      fontSize: '12px',
                                      padding: '2px 6px',
                                  },
                                  onClick: e => {
                                      e.stopPropagation()
                                      cancelEditMode()
                                  },
                              },
                              'Cancel'
                          )
                      )
                  ),
            React.createElement('input', {
                ref: fileInputRef,
                type: 'file',
                accept: '.zip',
                style: { display: 'none' },
                onChange: e => {
                    const file = e.target.files[0]
                    if (file) handleUpload(file)
                },
            })
        )
    }

    // Loading SCORM content
    if (loadingContent) {
        return React.createElement(
            'div',
            {
                style: {
                    padding: '40px',
                    textAlign: 'center',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                },
            },
            React.createElement('div', {
                style: {
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e2e8f0',
                    borderTopColor: '#0ea5e9',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 12px',
                },
            }),
            React.createElement(
                'p',
                { style: { margin: 0, color: '#64748b' } },
                'Loading SCORM content...'
            )
        )
    }

    // Package uploaded — render iframe with `src` (backend injects SCORM API into all HTML files).
    // `srcDoc` is intentionally NOT used: Rustici/SCORM Engine drivers load nested iframes
    // (scormcontent/index.html) from the R2 origin, which becomes cross-origin if the outer
    // iframe inherits the parent's origin via srcDoc — leading to SecurityError on driver navigation.
    if (scormSrc) {
        return React.createElement(
            'div',
            {
                style: {
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                },
            },
            React.createElement('iframe', {
                ref: iframeRef,
                src: scormSrc,
                style: {
                    width: '100%',
                    height: iframeHeight,
                    border: 'none',
                    display: 'block',
                },
                allowFullScreen: true,
                title: config.title || 'SCORM Content',
            }),
            // Delete button — clears scormConfig, snapshot listener fires DELETE,
            // widget re-renders with empty config → upload placeholder reappears.
            React.createElement(
                'button',
                {
                    type: 'button',
                    title: 'Remove SCORM',
                    'aria-label': 'Remove SCORM',
                    style: {
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '28px',
                        height: '28px',
                        padding: 0,
                        background: 'rgba(255,255,255,0.95)',
                        border: '1px solid #1f2937',
                        borderRadius: '4px',
                        fontSize: '14px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: '#000',
                        zIndex: 10,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                    onMouseDown: e => e.stopPropagation(),
                    onClick: e => {
                        e.stopPropagation()
                        enterEditMode()
                    },
                },
                '✕'
            )
        )
    }

    // Fallback — fetch failed, show info card
    const fullUrl = getFullEntryPointUrl(config.entryPointUrl)
    return React.createElement(
        'div',
        {
            style: {
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
            },
        },
        fullUrl
            ? React.createElement('iframe', {
                  src: fullUrl,
                  style: {
                      width: '100%',
                      height: '500px',
                      border: 'none',
                      display: 'block',
                  },
                  allowFullScreen: true,
                  title: config.title || 'SCORM Content',
              })
            : React.createElement(
                  'div',
                  {
                      style: {
                          padding: '40px',
                          textAlign: 'center',
                          color: '#64748b',
                      },
                  },
                  'No entry point URL available'
              )
    )
}

// ── Config Modal React component ──
function ScormConfigModal({ config, editor, onClose }) {
    const [uploading, setUploading] = useState(false)
    const [uploadPercent, setUploadPercent] = useState(0)
    const [currentConfig, setCurrentConfig] = useState({
        ...DEFAULT_SCORM_CONFIG,
        ...config,
    })
    const [dragOver, setDragOver] = useState(false)
    const fileInputRef = useRef(null)

    const handleFileSelect = file => {
        if (!file || !file.name.toLowerCase().endsWith('.zip')) {
            return
        }

        setUploading(true)
        setUploadPercent(0)

        // Use the scormUploadAction from Redux store via editor config
        const dispatch = editor.config.get('scormDispatch')
        if (dispatch) {
            const blockId = currentConfig.blockId || generateBlockId()
            const { scormUploadAction } = require('../../../../../reducers/scorm/action')
            dispatch(
                scormUploadAction(file, blockId, (response, error) => {
                    setUploading(false)
                    setUploadPercent(0)
                    if (error || !response) return
                    setCurrentConfig(prev => ({
                        ...prev,
                        blockId: response.blockId || blockId,
                        packageId: response.id,
                        title: response.title || file.name.replace('.zip', ''),
                        entryPointUrl: response.entryPointUrl || '',
                        version: response.version || '',
                        fileName: file.name,
                    }))
                }, setUploadPercent)
            )
        } else {
            // Fallback: simulate upload when no dispatch available
            setTimeout(() => {
                setUploading(false)
                setCurrentConfig(prev => ({
                    ...prev,
                    packageId: 'temp-' + Date.now(),
                    title: file.name.replace('.zip', ''),
                    fileName: file.name,
                }))
            }, 1500)
        }
    }

    const handleSave = () => {
        // Find the selected scormPackage model element and update its config
        const selection = editor.model.document.selection
        let scormElement = selection.getSelectedElement()

        if (!scormElement || scormElement.name !== 'scormPackage') {
            // Try to find it in ancestors
            const root = editor.model.document.getRoot()
            const range = editor.model.createRangeIn(root)
            for (const value of range.getWalker()) {
                if (value.item.is && value.item.is('element', 'scormPackage')) {
                    scormElement = value.item
                    break
                }
            }
        }

        if (scormElement) {
            editor.model.change(writer => {
                writer.setAttribute(
                    'scormConfig',
                    JSON.stringify(currentConfig),
                    scormElement
                )
            })
        }

        onClose()
    }

    const handleDrop = e => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files[0]
        if (file) handleFileSelect(file)
    }

    return React.createElement(
        'div',
        {
            style: {
                padding: '24px',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                minHeight: '300px',
            },
        },
        // Header
        React.createElement(
            'div',
            {
                style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                },
            },
            React.createElement(
                'h3',
                { style: { margin: 0, fontSize: '18px' } },
                'SCORM Package Settings'
            ),
            React.createElement(
                'button',
                {
                    style: {
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '20px',
                        color: '#64748b',
                    },
                    onClick: onClose,
                },
                '\u2715'
            )
        ),

        // Upload area
        !currentConfig.packageId &&
            React.createElement(
                'div',
                {
                    style: {
                        padding: '40px 20px',
                        border: `2px dashed ${dragOver ? '#0ea5e9' : '#cbd5e1'}`,
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: dragOver ? '#f0f9ff' : '#f8fafc',
                        marginBottom: '20px',
                        transition: 'all 0.2s ease',
                    },
                    onClick: () => fileInputRef.current && fileInputRef.current.click(),
                    onDragOver: e => {
                        e.preventDefault()
                        setDragOver(true)
                    },
                    onDragLeave: () => setDragOver(false),
                    onDrop: handleDrop,
                },
                uploading
                    ? React.createElement(
                          'div',
                          { style: { width: '60%', maxWidth: '320px', margin: '0 auto' } },
                          React.createElement(
                              'div',
                              {
                                  style: {
                                      width: '40px',
                                      height: '40px',
                                      border: '3px solid #e2e8f0',
                                      borderTopColor: '#0ea5e9',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      margin: '0 auto 12px',
                                  },
                              }
                          ),
                          React.createElement(
                              'p',
                              { style: { margin: '0 0 8px', color: '#64748b', fontWeight: 600 } },
                              uploadPercent < 100
                                  ? `Uploading… ${uploadPercent}%`
                                  : 'Processing…'
                          ),
                          React.createElement(
                              'div',
                              {
                                  style: {
                                      width: '100%',
                                      height: '6px',
                                      background: '#e2e8f0',
                                      borderRadius: '3px',
                                      overflow: 'hidden',
                                  },
                              },
                              React.createElement('div', {
                                  style: {
                                      width: `${uploadPercent}%`,
                                      height: '100%',
                                      background: '#0ea5e9',
                                      transition: 'width 200ms ease',
                                  },
                              })
                          )
                      )
                    : React.createElement(
                          'div',
                          {},
                          React.createElement(
                              'p',
                              {
                                  style: {
                                      fontSize: '32px',
                                      margin: '0 0 8px',
                                  },
                              },
                              '\uD83D\uDCC1'
                          ),
                          React.createElement(
                              'p',
                              {
                                  style: {
                                      margin: '0 0 4px',
                                      fontWeight: 600,
                                      color: '#334155',
                                  },
                              },
                              'Drop your SCORM .zip file here'
                          ),
                          React.createElement(
                              'p',
                              {
                                  style: {
                                      margin: 0,
                                      fontSize: '12px',
                                      color: '#94a3b8',
                                  },
                              },
                              'or click to browse'
                          )
                      ),
                React.createElement('input', {
                    ref: fileInputRef,
                    type: 'file',
                    accept: '.zip',
                    style: { display: 'none' },
                    onChange: e => {
                        const file = e.target.files[0]
                        if (file) handleFileSelect(file)
                    },
                })
            ),

        // Package info (shown after upload)
        currentConfig.packageId &&
            React.createElement(
                'div',
                {
                    style: {
                        padding: '16px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        marginBottom: '20px',
                    },
                },
                React.createElement(
                    'div',
                    {
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                        },
                    },
                    React.createElement(
                        'span',
                        { style: { color: '#16a34a', fontSize: '16px' } },
                        '\u2713'
                    ),
                    React.createElement(
                        'span',
                        {
                            style: {
                                fontWeight: 600,
                                color: '#166534',
                            },
                        },
                        'Package uploaded successfully'
                    )
                ),
                React.createElement(
                    'p',
                    {
                        style: {
                            margin: 0,
                            fontSize: '13px',
                            color: '#64748b',
                        },
                    },
                    `File: ${currentConfig.fileName}`
                )
            ),

        // Settings
        currentConfig.packageId &&
            React.createElement(
                'div',
                { style: { marginBottom: '20px' } },
                React.createElement(
                    'div',
                    { style: { marginBottom: '12px' } },
                    React.createElement(
                        'label',
                        {
                            style: {
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: 600,
                                marginBottom: '4px',
                                color: '#334155',
                            },
                        },
                        'Title'
                    ),
                    React.createElement('input', {
                        type: 'text',
                        value: currentConfig.title,
                        onChange: e =>
                            setCurrentConfig(prev => ({
                                ...prev,
                                title: e.target.value,
                            })),
                        style: {
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                        },
                    })
                ),
                React.createElement(
                    'div',
                    {
                        style: {
                            display: 'flex',
                            gap: '12px',
                        },
                    },
                    React.createElement(
                        'div',
                        { style: { flex: 1 } },
                        React.createElement(
                            'label',
                            {
                                style: {
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    marginBottom: '4px',
                                    color: '#334155',
                                },
                            },
                            'Width'
                        ),
                        React.createElement('input', {
                            type: 'text',
                            value: currentConfig.width,
                            onChange: e =>
                                setCurrentConfig(prev => ({
                                    ...prev,
                                    width: e.target.value,
                                })),
                            style: {
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                            },
                        })
                    ),
                    React.createElement(
                        'div',
                        { style: { flex: 1 } },
                        React.createElement(
                            'label',
                            {
                                style: {
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    marginBottom: '4px',
                                    color: '#334155',
                                },
                            },
                            'Height'
                        ),
                        React.createElement('input', {
                            type: 'text',
                            value: currentConfig.height,
                            onChange: e =>
                                setCurrentConfig(prev => ({
                                    ...prev,
                                    height: e.target.value,
                                })),
                            style: {
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                            },
                        })
                    )
                )
            ),

        // Action buttons
        React.createElement(
            'div',
            {
                style: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                },
            },
            React.createElement(
                'button',
                {
                    style: {
                        padding: '8px 16px',
                        background: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                    },
                    onClick: onClose,
                },
                'Cancel'
            ),
            currentConfig.packageId &&
                React.createElement(
                    'button',
                    {
                        style: {
                            padding: '8px 16px',
                            background: '#0ea5e9',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                        },
                        onClick: handleSave,
                    },
                    'Save'
                )
        )
    )
}

// ── CKEditor Plugin ──
export default class ScormPlugin extends Plugin {
    static get pluginName() {
        return 'ScormPlugin'
    }

    static get requires() {
        return [Widget]
    }

    constructor(editor) {
        super(editor)
        this.mountedRoots = new Map()
        this._updateTimeout = null
        this._lastConfigStr = null
    }

    init() {
        const editor = this.editor

        // Expose SCORM API stubs early so iframes can find them immediately
        if (!window.API) {
            window.API = {
                LMSInitialize: () => 'true',
                LMSFinish: () => 'true',
                LMSGetValue: () => '',
                LMSSetValue: () => 'true',
                LMSCommit: () => 'true',
                LMSGetLastError: () => '0',
                LMSGetErrorString: () => 'No Error',
                LMSGetDiagnostic: () => '',
            }
        }
        if (!window.API_1484_11) {
            window.API_1484_11 = {
                Initialize: () => 'true',
                Terminate: () => 'true',
                GetValue: () => '',
                SetValue: () => 'true',
                Commit: () => 'true',
                GetLastError: () => '0',
                GetErrorString: () => 'No Error',
                GetDiagnostic: () => '',
            }
        }

        editor.model.schema.register('scormPackage', {
            isObject: true,
            allowWhere: '$block',
            allowIn: 'column',
            allowAttributes: ['id', 'data', 'scormConfig'],
        })

        this._defineConverters()
        this._setupAttributeListener()
        this._setupRemovalListener()

        // Prevent keyboard deletion
        editor.editing.view.document.on(
            'keydown',
            (evt, data) => {
                const selection = editor.model.document.selection
                const selectedElement = selection.getSelectedElement()

                if (
                    selectedElement &&
                    selectedElement.name === 'scormPackage'
                ) {
                    const domEvent = data.domEvent
                    const key = domEvent.key

                    const allowedKeys = [
                        'Escape',
                        'ArrowUp',
                        'ArrowDown',
                        'ArrowLeft',
                        'ArrowRight',
                        'Tab',
                    ]
                    if (allowedKeys.includes(key)) {
                        return
                    }

                    if (
                        key === 'Delete' ||
                        key === 'Backspace' ||
                        (key.length === 1 &&
                            !domEvent.ctrlKey &&
                            !domEvent.metaKey)
                    ) {
                        evt.stop()
                        data.preventDefault()
                    }
                }
            },
            { priority: 'highest' }
        )

        // Insert command
        editor.commands.add('insertScormPackage', {
            execute: config => {
                editor.model.change(writer => {
                    const baseConfig = config || DEFAULT_SCORM_CONFIG
                    const scormConfig = baseConfig.blockId
                        ? baseConfig
                        : { ...baseConfig, blockId: generateBlockId() }
                    const simpleBoxWrapper = writer.createElement(
                        'simpleBoxWrapper',
                        {
                            backgroundColor: '#f0f9ff',
                        }
                    )
                    const simpleBox = writer.createElement('simpleBox')
                    const column = writer.createElement('column')
                    const scormPackage = writer.createElement('scormPackage', {
                        scormConfig: JSON.stringify(scormConfig),
                    })

                    writer.append(simpleBox, simpleBoxWrapper)
                    writer.append(column, simpleBox)
                    writer.append(scormPackage, column)

                    editor.model.insertContent(simpleBoxWrapper)
                    writer.setSelection(scormPackage, 'on')
                })
            },
        })
    }

    _setupRemovalListener() {
        const editor = this.editor
        // Map<blockId, packageId|null>
        this._trackedPackages = new Map()

        editor.model.document.on('change:data', () => {
            const current = new Map()
            const root = editor.model.document.getRoot()
            if (root) {
                const range = editor.model.createRangeIn(root)
                for (const value of range.getWalker()) {
                    if (
                        value.item &&
                        value.item.is &&
                        value.item.is('element', 'scormPackage')
                    ) {
                        const configStr = value.item.getAttribute('scormConfig')
                        if (!configStr) continue
                        try {
                            const cfg = JSON.parse(configStr)
                            if (cfg.blockId) {
                                current.set(cfg.blockId, cfg.packageId || null)
                            }
                        } catch (e) {
                            /* ignore malformed config */
                        }
                    }
                }
            }

            // Set of packageIds still referenced anywhere in the document.
            // Used to skip DELETE when the same package is duplicated (e.g. copy/paste).
            const livePackageIds = new Set()
            for (const pid of current.values()) {
                if (pid) livePackageIds.add(pid)
            }

            const getCardId = editor.config.get('scormGetCardId')
            const cardId = typeof getCardId === 'function' ? getCardId() : null
            const { scormDeleteService } = require('../../../../../api/scorm')
            const queuedDeletes = new Set()
            for (const [blockId, oldPackageId] of this._trackedPackages) {
                const newPackageId = current.get(blockId)
                if (!oldPackageId || oldPackageId === newPackageId) continue
                // Skip if the same package is still referenced by another block
                // (copy/paste, or a different blockId pointing to it).
                if (livePackageIds.has(oldPackageId)) continue
                if (queuedDeletes.has(oldPackageId)) continue
                queuedDeletes.add(oldPackageId)
                // The backend refuses while another page still uses the package; that is expected,
                // so a refusal is not shown to the user.
                scormDeleteService(oldPackageId, cardId).catch(() => {})
            }

            this._trackedPackages = current
        })
    }

    _setupAttributeListener() {
        const editor = this.editor

        editor.model.document.on('change:data', () => {
            const changes = editor.model.document.differ.getChanges()

            for (const change of changes) {
                if (
                    change.type === 'attribute' &&
                    change.attributeKey === 'scormConfig'
                ) {
                    const modelElement = change.range.start.nodeAfter
                    if (
                        modelElement &&
                        modelElement.name === 'scormPackage'
                    ) {
                        const newConfig =
                            modelElement.getAttribute('scormConfig')
                        this._updateScormComponent(modelElement, newConfig)
                    }
                }
            }
        })
    }

    _updateScormComponent(modelElement, scormConfig) {
        if (this._lastConfigStr === scormConfig) {
            return
        }
        this._lastConfigStr = scormConfig

        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout)
        }

        this._updateTimeout = setTimeout(() => {
            const editor = this.editor
            const viewElement =
                editor.editing.mapper.toViewElement(modelElement)
            if (!viewElement) return

            const domConverter = editor.editing.view.domConverter
            const containerDom = domConverter.mapViewToDom(viewElement)
            if (!containerDom) return

            containerDom.setAttribute('data-scorm-config', scormConfig)

            const placeholder = containerDom.querySelector(
                '.scorm-react-placeholder'
            )
            if (!placeholder) return

            const existingRoot = this.mountedRoots.get(placeholder)
            if (existingRoot) {
                try {
                    const parsedConfig = scormConfig
                        ? JSON.parse(scormConfig)
                        : DEFAULT_SCORM_CONFIG

                    existingRoot.unmount()
                    this.mountedRoots.delete(placeholder)

                    const newRoot = createRoot(placeholder)
                    newRoot.render(
                        React.createElement(ScormEditorWidget, {
                            key: `scorm-${Date.now()}`,
                            config: parsedConfig,
                            editor,
                        })
                    )
                    this.mountedRoots.set(placeholder, newRoot)
                } catch (error) {
                    console.error('Error re-rendering SCORM component:', error)
                }
            }
        }, 500)
    }

    _defineConverters() {
        const editor = this.editor
        const conversion = editor.conversion

        // Upcast: HTML -> Model
        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                attributes: {
                    'data-type': 'scorm-package',
                },
            },
            model: (viewElement, { writer }) => {
                const id = viewElement.getAttribute('id')
                const data = viewElement.getAttribute('data') || ''
                const scormConfig =
                    viewElement.getAttribute('data-scorm-config') || ''

                const attrs = {
                    data,
                    scormConfig,
                }
                if (id) attrs.id = id

                return writer.createElement('scormPackage', attrs)
            },
        })

        // Data Downcast: Model -> HTML (for saving)
        conversion.for('dataDowncast').elementToElement({
            model: 'scormPackage',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id')
                const data = modelElement.getAttribute('data') || ''
                const scormConfig =
                    modelElement.getAttribute('scormConfig') || ''

                const attrs = {
                    'data-type': 'scorm-package',
                    data: data,
                    'data-scorm-config': scormConfig,
                }

                if (id !== undefined && id !== null) {
                    attrs.id = id
                }

                return writer.createEmptyElement('div', attrs)
            },
        })

        // Editing Downcast: Model -> Interactive View
        conversion.for('editingDowncast').elementToElement({
            model: 'scormPackage',
            view: (modelElement, { writer }) => {
                const scormConfig =
                    modelElement.getAttribute('scormConfig') || ''

                const containerAttrs = {
                    class: 'scorm-widget-container',
                    'data-type': 'scorm-package',
                    'data-scorm-config': scormConfig,
                }

                const container = writer.createContainerElement(
                    'div',
                    containerAttrs
                )

                const placeholder = writer.createRawElement(
                    'div',
                    {
                        class: 'scorm-react-placeholder',
                    },
                    domElement => {
                        // Prevent events from reaching CKEditor
                        domElement.addEventListener('mousedown', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('click', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('dblclick', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('pointerdown', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('pointermove', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('pointerup', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('keydown', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('keyup', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener('input', e =>
                            e.stopPropagation()
                        )
                        domElement.addEventListener(
                            'focus',
                            e => e.stopPropagation(),
                            true
                        )
                        domElement.addEventListener(
                            'blur',
                            e => e.stopPropagation(),
                            true
                        )

                        // Mount React component
                        try {
                            const parsedConfig = scormConfig
                                ? JSON.parse(scormConfig)
                                : DEFAULT_SCORM_CONFIG

                            const root = createRoot(domElement)
                            root.render(
                                React.createElement(ScormEditorWidget, {
                                    key: Date.now(),
                                    config: parsedConfig,
                                    editor,
                                })
                            )

                            editor.plugins
                                .get('ScormPlugin')
                                .mountedRoots.set(domElement, root)
                        } catch (error) {
                            console.error(
                                'Error rendering SCORM component:',
                                error
                            )
                            domElement.innerHTML = `
                                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                                    <span style="font-size: 24px;">\uD83D\uDCE6</span>
                                    <p>SCORM Package</p>
                                    <small>Component not available</small>
                                </div>
                            `
                        }
                    }
                )

                writer.insert(
                    writer.createPositionAt(container, 0),
                    placeholder
                )

                return toWidget(container, writer, {
                    label: 'SCORM Package',
                    hasSelectionHandle: true,
                })
            },
        })
    }

    destroy() {
        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout)
            this._updateTimeout = null
        }

        for (const [, root] of this.mountedRoots) {
            try {
                root.unmount()
            } catch (error) {
                console.error('Error unmounting SCORM component:', error)
            }
        }
        this.mountedRoots.clear()
        super.destroy()
    }
}

export { ScormPlugin }
